// CellKebabMenu - Reusable dropdown kebab menu for cell action buttons

import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CheckIcon from '@mui/icons-material/Check';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { alpha, IconButton, Menu, MenuItem, type SvgIconProps, type SxProps, type Theme } from '@mui/material';
import { type ComponentType, memo, type ReactNode, useEffect, useRef, useState } from 'react';
import { LinesIconSvg } from '@/components/Icons/LinesIconSvg';
import { useColorMode } from '@/hooks';
import { BookmarkTagIcon, CharactersBadgeIcon, CreateAboveIcon, CreateBelowIcon, DeleteTrashIcon, LocationPinIcon } from './menuIcons';

export type MenuAction =
    | 'createAbove'
    | 'createBelow'
    | 'createLeft'
    | 'createRight'
    | 'createChild'
    | 'selectAll'
    | 'delete'
    | 'tags'
    | 'characters'
    | 'locations'
    | 'lines'
    | 'generate';

// Icon set mirrors the Figma card menu (issue #1799); design-kit SVGs live in menuIcons.tsx
const actionIconMap: Record<MenuAction, ComponentType<SvgIconProps>> = {
    createAbove: CreateAboveIcon,
    createBelow: CreateBelowIcon,
    createLeft: ArrowBackIcon,
    createRight: ArrowForwardIcon,
    createChild: AddBoxOutlinedIcon,
    selectAll: CheckIcon,
    delete: DeleteTrashIcon,
    tags: BookmarkTagIcon,
    characters: CharactersBadgeIcon,
    locations: LocationPinIcon,
    lines: LinesIconSvg,
    generate: AutoAwesomeOutlinedIcon,
};

export interface CellKebabMenuOption {
    label: string;
    onClick: () => void;
    action?: MenuAction;
    danger?: boolean;
    icon?: ReactNode;
    /** Shown but greyed out — the action exists here, just not right now. */
    disabled?: boolean;
}

export interface CellKebabMenuProps {
    options: CellKebabMenuOption[];
    iconSize?: string;
    menuIconSize?: string;
    /** Overrides the trigger button's colors (e.g. white icons on dark card header strips). */
    buttonSx?: SxProps<Theme>;
}

export const CellKebabMenu = memo(({ options, iconSize = '16px', menuIconSize = '16px', buttonSx }: CellKebabMenuProps) => {
    const isDarkMode = useColorMode();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const actionButtons = buttonRef.current?.closest('.action-buttons');
        if (!actionButtons) return;
        if (open) {
            actionButtons.classList.add('menu-open');
        } else {
            actionButtons.classList.remove('menu-open');
        }
    }, [open]);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <IconButton
                ref={buttonRef}
                size="small"
                onClick={handleOpen}
                sx={[
                    {
                        padding: '2px',
                        color: theme => theme.custom.c57,
                        '&:hover': {
                            color: theme => (isDarkMode ? theme.custom.c1 : theme.custom.c22),
                            backgroundColor: theme => alpha(theme.palette.action.hover, 0.15),
                        },
                    },
                    ...(buttonSx ? [buttonSx].flat() : []),
                ]}
            >
                <MoreVertIcon sx={{ fontSize: iconSize }} />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                disableScrollLock
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: '12px',
                            minWidth: '160px',
                            maxWidth: '280px',
                            backgroundColor: theme => (isDarkMode ? theme.palette.secondary.light : theme.custom.c24),
                            backdropFilter: 'blur(30px)',
                        },
                    },
                }}
            >
                {options.map(option => {
                    const isDanger = option.danger ?? option.action === 'delete';
                    const IconComponent = option.action ? actionIconMap[option.action] : null;
                    const resolvedIcon = option.icon ?? (IconComponent ? <IconComponent sx={{ fontSize: menuIconSize }} /> : null);

                    return (
                        <MenuItem
                            key={option.label}
                            disabled={option.disabled}
                            onClick={e => {
                                e.stopPropagation();
                                handleClose();
                                option.onClick();
                            }}
                            sx={{
                                fontSize: '13px',
                                padding: '8px 16px',
                                color: theme => (isDanger ? theme.palette.error.main : isDarkMode ? theme.custom.c1 : theme.custom.c22),
                            }}
                        >
                            <div className="flex items-center gap-[8px]">
                                {resolvedIcon}
                                {option.label}
                            </div>
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
});

CellKebabMenu.displayName = 'CellKebabMenu';
